import { ApiError, getToken } from '../api/client';
import { sessionsApi } from '../api/sessions';
import {
  EMPTY_PROF_FIGURE,
  parseProfFigureSnapshot,
  profFigureAssetRef,
  type ProfFigureCommand,
  type ProfFigureEvent,
  type ProfFigureSelection,
  type ProfFigureSnapshot,
} from '../types/prof-figure';
import { isScopedThreadId, useChat } from './chat';

export const FIGURE_CONFLICT_NOTICE = 'La figura è cambiata in un’altra finestra. Ho recuperato quella attuale: scegli di nuovo la figura da usare.';
const FIGURE_SAVE_NOTICE = 'Non riesco a salvare la figura. Riprova prima di inviare il messaggio.';
const pendingWrites = new Map<string, Promise<boolean>>();

export function profFigureSessionId(threadId: string): string | null {
  const s = useChat.getState();
  return isScopedThreadId(threadId) ? s.scopedSessionIds[threadId] ?? null : s.chiediSessionId;
}

function sameReference(a: ProfFigureSelection['reference'], b: ProfFigureSelection['reference']): boolean {
  return a.kind === b.kind && (a.kind === 'asset' && b.kind === 'asset'
    ? a.imageRef === b.imageRef
    : a.kind === 'question' && b.kind === 'question' && a.questionId === b.questionId);
}

/** Selection is explicit. No visible-asset list or assistant marker calls this. */
export function selectProfFigure(threadId: string, selection: ProfFigureSelection | null): Promise<boolean> {
  const state = useChat.getState();
  const sessionId = profFigureSessionId(threadId);
  const snapshot = sessionId ? state.profFiguresBySession[sessionId] ?? EMPTY_PROF_FIGURE : EMPTY_PROF_FIGURE;
  const imageRef = selection ? profFigureAssetRef(selection.imageRef) : null;
  if (selection && !imageRef) return Promise.resolve(false);
  state.setProfFigureNotice(threadId, null);
  // A never-sent, never-created thread has nothing to clear server-side.
  if (!selection && !sessionId) {
    state.setPendingProfFigure(threadId, null);
    return Promise.resolve(true);
  }
  if (!state.pendingProfFiguresByThread[threadId] && (
    (!selection && !snapshot.figure)
    || (selection && snapshot.figure && sameReference(selection.reference, snapshot.figure.reference))
  )) return Promise.resolve(true);
  const command: ProfFigureCommand = {
    mutationId: crypto.randomUUID(),
    expectedRevision: snapshot.revision,
    ...(selection ? { action: 'set' as const, reference: selection.reference } : { action: 'clear' as const }),
  };
  state.setPendingProfFigure(threadId, {
    command,
    preview: selection ? { reference: selection.reference, imageRef: imageRef!, label: selection.label } : null,
    status: 'ready',
  });
  return sessionId ? flushProfFigure(threadId) : Promise.resolve(true);
}

/** One write loop per thread, shared even if both Prof hosts are mounted. */
export function flushProfFigure(threadId: string): Promise<boolean> {
  const running = pendingWrites.get(threadId);
  if (running) return running;
  const sessionId = profFigureSessionId(threadId);
  if (!sessionId) return Promise.resolve(true);
  const capturedToken = getToken();
  const currentScope = () => getToken() === capturedToken && profFigureSessionId(threadId) === sessionId;
  const work = (async () => {
    while (currentScope()) {
      const pending = useChat.getState().pendingProfFiguresByThread[threadId];
      if (!pending) return true;
      useChat.getState().setPendingProfFigure(threadId, { ...pending, status: 'saving' });
      try {
        const result = await sessionsApi.updateFigureContext(sessionId, pending.command);
        if (!currentScope()) return false;
        const snapshot = parseProfFigureSnapshot(result.figureContext);
        if (!snapshot) throw new Error('Invalid figure context');
        useChat.getState().setProfFigureSnapshot(sessionId, snapshot);
        const latest = useChat.getState().pendingProfFiguresByThread[threadId];
        if (latest?.command.mutationId === pending.command.mutationId) {
          useChat.getState().setPendingProfFigure(threadId, null);
        } else if (latest && snapshot.lastMutationId === pending.command.mutationId) {
          // This newer local choice has not been sent. Chain it after OUR
          // confirmed write; never rebase after a conflict with another tab.
          useChat.getState().setPendingProfFigure(threadId, {
            ...latest,
            command: { ...latest.command, expectedRevision: snapshot.revision },
          });
        }
        useChat.getState().setProfFigureNotice(threadId, null);
      } catch (error) {
        if (!currentScope()) return false;
        if (error instanceof ApiError && error.status === 409) {
          const body = error.body as { figureContext?: unknown } | null;
          const snapshot = parseProfFigureSnapshot(body?.figureContext);
          if (snapshot) useChat.getState().setProfFigureSnapshot(sessionId, snapshot);
          useChat.getState().setPendingProfFigure(threadId, null);
          useChat.getState().setProfFigureNotice(threadId, FIGURE_CONFLICT_NOTICE);
        } else {
          const latest = useChat.getState().pendingProfFiguresByThread[threadId];
          if (latest) useChat.getState().setPendingProfFigure(threadId, { ...latest, status: 'error' });
          useChat.getState().setProfFigureNotice(threadId, FIGURE_SAVE_NOTICE);
        }
        return false;
      }
    }
    return false;
  })();
  pendingWrites.set(threadId, work);
  void work.finally(() => {
    if (pendingWrites.get(threadId) === work) pendingWrites.delete(threadId);
  });
  return work;
}

/** Resolve outstanding PATCH before a real chat turn; fresh threads carry set. */
export async function prepareProfFigureTurn(threadId: string): Promise<{
  ok: boolean;
  command?: ProfFigureCommand;
}> {
  if (profFigureSessionId(threadId)) return { ok: await flushProfFigure(threadId) };
  const pending = useChat.getState().pendingProfFiguresByThread[threadId];
  return { ok: true, ...(pending ? { command: pending.command } : {}) };
}

export function receiveProfFigureEvent(threadId: string, event: ProfFigureEvent): void {
  if (profFigureSessionId(threadId) !== event.sessionId) return;
  const state = useChat.getState();
  state.setProfFigureSnapshot(event.sessionId, event.snapshot, event.imageStatus);
  const pending = state.pendingProfFiguresByThread[threadId];
  if (pending && pending.command.mutationId === event.snapshot.lastMutationId) {
    useChat.getState().setPendingProfFigure(threadId, null);
    useChat.getState().setProfFigureNotice(threadId, null);
  }
}

export function receiveProfFigureError(threadId: string, error: string): boolean {
  if (/figure.*(?:conflict|revision)|revision_conflict/.test(error)) {
    useChat.getState().setPendingProfFigure(threadId, null);
    useChat.getState().setProfFigureNotice(threadId, FIGURE_CONFLICT_NOTICE);
    return true;
  }
  if (!error.startsWith('figure_')) return false;
  const pending = useChat.getState().pendingProfFiguresByThread[threadId];
  if (pending) useChat.getState().setPendingProfFigure(threadId, { ...pending, status: 'error' });
  useChat.getState().setProfFigureNotice(threadId, FIGURE_SAVE_NOTICE);
  return true;
}

/** Called by resume so a historical thread never inherits another's selection. */
export function figureStateForResume(sessionId: string, raw: unknown): ProfFigureSnapshot {
  const server = parseProfFigureSnapshot(raw) ?? EMPTY_PROF_FIGURE;
  const state = useChat.getState();
  const local = state.profFiguresBySession[sessionId];
  return local && state.chiediSessionId === sessionId && local.revision > server.revision ? local : server;
}
